// Lib
import React, { useMemo } from 'react';
import { convertToRaw } from 'draft-js';

// Utils
import { getCellTextAlignment } from '../utils/tableCellFormattingUtils';
import { getCellRenderContentState } from '../utils/tableCellRenderValueUtils';
import getAllContentInlineStyle from '../../../components/editor/customRichUtils/contentState/getAllContentInlineStyle';
import getLinksFromText from '../../../../common/utils/getLinksFromText';
import rawGetInlineLinks from '../../../../common/utils/editor/rawUtils/rawGetInlineLinks';

// Components
import TableCellEditor from '../../../components/editor/tableCell/TableCellEditor';

// Hooks
import { MilanoteHotTableInstance } from './useMilanoteCellRendererFormulaInfo';

// Constants
import { TABLE_CELL_EDITOR_KEY } from '../../../../common/table/tableConstants';

// Types
import { CellData, CellFormulaInfo, HotComputedCellValue } from '../../../../common/table/TableTypes';

type Props = {
    hotComputedCellValue: HotComputedCellValue;
    cellData?: CellData;
    hotTableInstance: MilanoteHotTableInstance;
    filterQuery?: string;
    cellFormulaInfo?: CellFormulaInfo;
};

const CellTextRenderer: React.FC<Props> = (props) => {
    const { hotComputedCellValue, cellData = { value: null }, hotTableInstance, filterQuery, cellFormulaInfo } = props;
    const { textStyle } = cellData;
    const { locale, elementId } = hotTableInstance.milanoteProps;

    const { rawContent, plainText, shouldRenderPlainText } = useMemo(() => {
        const contentState = getCellRenderContentState(cellData, locale, hotComputedCellValue, cellFormulaInfo);

        if (contentState === null) return {};

        const rawContent = convertToRaw(contentState);
        const plainText = contentState.getPlainText();

        const inlineStyles = getAllContentInlineStyle(contentState);

        const hasTextStyling = (!!inlineStyles && inlineStyles.size > 0) || (!!textStyle && textStyle.length > 0);
        const hasLinks =
            getLinksFromText(plainText).length > 0 ||
            (!!cellData.draftContent && rawGetInlineLinks(cellData.draftContent).length > 0);

        const shouldRenderPlainText = !hasTextStyling && !hasLinks;

        return { rawContent, plainText, shouldRenderPlainText };
    }, [hotComputedCellValue, cellData, locale, cellFormulaInfo, textStyle]);

    const editorTextAlignment = useMemo(
        () => getCellTextAlignment(cellData, locale, hotComputedCellValue),
        [hotComputedCellValue, cellData, locale],
    );

    if (rawContent === undefined) {
        return null;
    }

    if (!filterQuery && shouldRenderPlainText) {
        return <p style={{ textAlign: editorTextAlignment }}>{plainText}</p>;
    }

    return (
        <TableCellEditor
            className="handsontable-renderer"
            isEditing={false}
            isEditable={false}
            textContent={rawContent}
            defaultStyleOverride={textStyle}
            textAlignment={editorTextAlignment}
            editorId={`${elementId}-${TABLE_CELL_EDITOR_KEY}-RENDERER`}
            editorKey={`${TABLE_CELL_EDITOR_KEY}-RENDERER`}
            filterQuery={filterQuery}
        />
    );
};

export default CellTextRenderer;
